import type { AssetLocation } from '../../../data/locations.tsx'

export type MapSelectionMode = 'click' | 'drag'

export type PixelPoint = { x: number; y: number }

export type PixelRectangle = { left: number; top: number; right: number; bottom: number }

const MIN_RECTANGLE_PIXELS = 5

export const rectangleBetween = (a: PixelPoint, b: PixelPoint): PixelRectangle => ({
	left: Math.min(a.x, b.x),
	top: Math.min(a.y, b.y),
	right: Math.max(a.x, b.x),
	bottom: Math.max(a.y, b.y),
})

export const isRectangle = ({ left, top, right, bottom }: PixelRectangle) =>
	right - left >= MIN_RECTANGLE_PIXELS && bottom - top >= MIN_RECTANGLE_PIXELS

type Positioned = Pick<AssetLocation, 'id'>

// The assets inside the rectangle that are not selected yet. When there is not room for all of them, the ones nearest its center are kept
export const locationsToAdd = <T extends Positioned>(
	locations: T[],
	selected: Positioned[],
	rectangle: PixelRectangle,
	project: (location: T) => PixelPoint,
	maxAssets: number,
): T[] => {
	const selectedIds = new Set(selected.map(({ id }) => id))
	const centerX = (rectangle.left + rectangle.right) / 2
	const centerY = (rectangle.top + rectangle.bottom) / 2

	return locations
		.filter(({ id }) => !selectedIds.has(id))
		.map((location) => ({ location, point: project(location) }))
		.filter(
			({ point }) => point.x >= rectangle.left && point.x <= rectangle.right && point.y >= rectangle.top && point.y <= rectangle.bottom,
		)
		.sort((a, b) => Math.hypot(a.point.x - centerX, a.point.y - centerY) - Math.hypot(b.point.x - centerX, b.point.y - centerY))
		.slice(0, Math.max(0, maxAssets - selected.length))
		.map(({ location }) => location)
}
